/**
 * Source with syntax colouring, for read-only display in Ask Lilia fences.
 *
 * Uses PrismLight with only the grammars we actually have: LaTeX (which also
 * answers to `tex` and `context`) and LML, whose grammar is ours — Prism does
 * not ship one. The plain `Prism` export bundles every language it knows;
 * this carries two.
 *
 * Colour, not weight. Two earlier attempts fixed the "code looks washed out"
 * report with CSS — darker ink, then bold in #239, reverted in #240 — and both
 * were wrong. The ink was already the darkest in the palette at 13.9:1, and
 * measured against the reference block this was the same size, the same
 * density, and a HIGHER contrast ratio. What the reference had was colour.
 *
 * Colours are the existing --lilia-code-* tokens, not new ones. They already
 * dress the CodeMirror surfaces, so the same source cannot look like two
 * different languages depending on whether it happens to be editable, and
 * light/dark comes free across all four themes.
 *
 * Restraint is the design. Prism tags every LaTeX control sequence as
 * `function`, and a real document is mostly control sequences, so colouring
 * them all equally yields a rainbow that reads worse than plain text. The
 * palette is ranked by how much meaning a token carries:
 *
 *   keyword      environment / label / ref / package names, section titles,
 *                and LML @blocktypes — what the construct IS. The strongest
 *                colour, because that is what anyone is scanning for.
 *   equation     math spans, tinted as a unit so a formula reads as one
 *                object rather than a field of backslashes.
 *   math command commands inside math, separated from the math around them.
 *   attr-name    the key in an LML [key=value].
 *   comment      muted and italic — present, not competing.
 *   function     every other \command. The quietest coloured token.
 *   punctuation  deliberately NOT styled. Braces and brackets inherit the
 *                body colour. Colouring these is what turns syntax colour
 *                into confetti.
 */
import { PrismLight as SyntaxHighlighter } from "react-syntax-highlighter";
import latex from "react-syntax-highlighter/dist/esm/languages/prism/latex";
import lml from "@/lib/prism-lml";

SyntaxHighlighter.registerLanguage("latex", latex);
SyntaxHighlighter.registerLanguage("lml", lml);

const LATEX_LANGS = new Set(["latex", "tex", "context", "math"]);
const LML_LANGS = new Set(["lml"]);

export function isLatexLang(lang: string): boolean {
  return LATEX_LANGS.has(lang.trim().toLowerCase());
}

export function isLmlLang(lang: string): boolean {
  return LML_LANGS.has(lang.trim().toLowerCase());
}

/**
 * The Prism language for a fence tag, or null to leave it plain.
 *
 * Only languages we have a grammar for are claimed. Anything else stays
 * uncoloured rather than being half-highlighted by a grammar that does not fit
 * it, which reads worse than no colour at all.
 */
export function fenceLanguage(lang: string): "latex" | "lml" | null {
  if (isLatexLang(lang)) return "latex";
  if (isLmlLang(lang)) return "lml";
  return null;
}

/**
 * Prism theme object: keys are token class names, values are style objects.
 *
 * Not one of the bundled themes (oneDark and friends) — those hard-code hex
 * values chosen for their own dark background and would fight every Lilia
 * theme in both modes.
 */
const LILIA_CODE_THEME: Record<string, React.CSSProperties> = {
  comment: { color: "var(--lilia-code-comment)", fontStyle: "italic" },
  cdata: { color: "var(--lilia-code-comment)" },

  // LaTeX: \begin{…} \end{…} \label{…} \ref{…} \cite{…} \usepackage{…}
  // arguments and \section{…} titles. LML: @blocktype.
  keyword: { color: "var(--lilia-code-tag)" },
  "class-name": { color: "var(--lilia-code-tag)" },

  // Math spans, and LML quoted attribute values.
  equation: { color: "var(--lilia-code-string)" },
  string: { color: "var(--lilia-code-string)" },

  // Commands appearing inside a math span.
  "equation-command": { color: "var(--lilia-code-mathdelim)" },
  regex: { color: "var(--lilia-code-mathdelim)" },

  // Every other control sequence.
  function: { color: "var(--lilia-code-command)" },
  selector: { color: "var(--lilia-code-command)" },

  "attr-name": { color: "var(--lilia-code-attr-key)" },
  url: { color: "var(--lilia-code-type)", textDecoration: "underline" },

  // punctuation intentionally absent — inherits.
};

export function CodeFence({ code, lang }: { code: string; lang: string }) {
  const language = fenceLanguage(lang);
  if (!language) return null;

  return (
    <SyntaxHighlighter
      data-testid="code-fence"
      data-fence-language={language}
      language={language}
      style={LILIA_CODE_THEME}
      // The fence owns the box; this must not add one of its own.
      customStyle={{
        margin: 0,
        padding: "8px",
        background: "transparent",
        fontSize: "12.5px",
        lineHeight: 1.375,
        overflowX: "auto",
      }}
      codeTagProps={{
        style: { fontFamily: "inherit", background: "transparent", fontWeight: 400 },
      }}
    >
      {code}
    </SyntaxHighlighter>
  );
}
